import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { connect } from "react-redux";
import { Layout } from "views/common/Layout";
import { Card } from "views/components/ui/Card";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "views/components/ui/Tabs";
import {
    Download,
    Plus,
    Wrench,
    Bookmark,
    Eye,
    Edit,
    Loader2,
    SquareArrowOutUpRight,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import { fetchSavedTools } from "actions/tool";

const Dashboard = ({ savedTools, fetchSavedTools, userAuth }) => {
    const [activeTab, setActiveTab] = useState("submitted");
    const [loading, setLoading] = useState(true);
    const [submittedTools, setSubmittedTools] = useState([]);
    const history = useHistory();
    const dispatch = useDispatch();

    // Sample data - replace with actual data fetching
    const stats = {
        submittedTools: submittedTools.length,
        savedTools: savedTools.length,
    };

    useEffect(() => {
        fetchSubmittedTools();
        fetchSavedTools(userAuth.userAccount.uuid);
    }, [dispatch]);

    const fetchSubmittedTools = async () => {
        try {
            setLoading(true);
            if (userAuth?.uuid) {
                await dispatch(fetchSubmittedTools(userAuth.uuid));
            }
        } catch (err) {
            console.error("Error fetching submitted tools:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitTool = () => {
        history.push("/submit");
    };

    const renderSubmittedTools = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
            );
        }

        if (submittedTools.length === 0) {
            return (
                <div className="text-center py-12">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                        No tools submitted yet
                    </h3>
                    <p className="text-gray-600 mb-6">
                        Start by submitting your first tool to the platform
                    </p>
                    <button
                        onClick={handleSubmitTool}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Submit Tool
                    </button>
                </div>
            );
        }

        return (
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="text-left text-sm text-gray-500">
                            <th className="pb-4">Name</th>
                            <th className="pb-4">Status</th>
                            <th className="pb-4">Submissions</th>
                            <th className="pb-4">Revenue</th>
                            <th className="pb-4">Last Updated</th>
                            <th className="pb-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {submittedTools.map((tool) => (
                            <tr key={tool.id} className="border-t">
                                <td className="py-4">{tool.name}</td>
                                <td className="py-4">
                                    <span
                                        className={`px-2 py-1 rounded-full text-xs ${
                                            tool.status === "Active"
                                                ? "bg-green-100 text-green-800"
                                                : "bg-yellow-100 text-yellow-800"
                                        }`}
                                    >
                                        {tool.status}
                                    </span>
                                </td>
                                <td className="py-4">{tool.submissions}</td>
                                <td className="py-4">${tool.revenue}</td>
                                <td className="py-4">{tool.lastUpdated}</td>
                                <td className="py-4">
                                    <button className="text-blue-600 hover:text-blue-700">
                                        <Eye className="h-4 w-4" />
                                    </button>
                                    <button className="text-blue-600 hover:text-blue-700">
                                        <Edit className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderSavedTools = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
            );
        }

        if (savedTools.length === 0) {
            return (
                <div className="text-center py-12">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                        No saved tools yet
                    </h3>
                    <p className="text-gray-600 mb-6">
                        Start by saving tools you find interesting
                    </p>
                </div>
            );
        }

        return (
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="text-left text-sm text-gray-500">
                            <th className="pb-4">Name</th>
                            <th className="pb-4">Category</th>
                            <th className="pb-4">Status</th>
                            <th className="pb-4">Pricing</th>
                            <th className="pb-4">Saved At</th>
                            <th className="pb-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {savedTools.map((tool) => (
                            <tr key={tool.id} className="border-t">
                                <td className="py-4">{tool.name}</td>
                                <td className="py-4">{tool.category}</td>
                                <td className="py-4">
                                    <span
                                        className={`px-2 py-1 rounded-full text-xs ${
                                            tool.status === "Active"
                                                ? "bg-green-100 text-green-800"
                                                : "bg-yellow-100 text-yellow-800"
                                        }`}
                                    >
                                        {tool.status}
                                    </span>
                                </td>
                                <td className="py-4">
                                    {tool.pricingModel === "Free"
                                        ? "Free"
                                        : `${tool.pricingCurrency} ${
                                              tool.minPrice
                                          }${
                                              tool.maxPrice
                                                  ? ` - ${tool.maxPrice}`
                                                  : "+"
                                          }`}
                                </td>
                                <td className="py-4">
                                    {new Date(
                                        tool.savedAt
                                    ).toLocaleDateString()}
                                </td>
                                <td className="py-4">
                                    <button
                                        onClick={() =>
                                            window.open(tool.url, "_blank")
                                        }
                                        className="text-blue-600 hover:text-blue-700 mr-2"
                                    >
                                        <SquareArrowOutUpRight className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <Layout>
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold">Dashboard</h1>
                    <div className="flex gap-4">
                        <button className="flex items-center text-sm text-blue-600 hover:text-blue-700">
                            <Download className="h-4 w-4 mr-2" />
                            Export Data
                        </button>
                        <button
                            onClick={handleSubmitTool}
                            className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Add New Tool
                        </button>
                    </div>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <Card className="p-6">
                        <div className="flex items-center gap-3">
                            <Wrench className="h-6 w-6 text-blue-600" />
                            <div>
                                <h3 className="text-lg font-medium">
                                    Tools Submitted
                                </h3>
                                <p className="text-2xl font-bold">
                                    {stats.submittedTools}
                                </p>
                            </div>
                        </div>
                    </Card>
                    <Card className="p-6">
                        <div className="flex items-center gap-3">
                            <Bookmark className="h-6 w-6 text-green-600" />
                            <div>
                                <h3 className="text-lg font-medium">
                                    Tools Saved
                                </h3>
                                <p className="text-2xl font-bold">
                                    {stats.savedTools}
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Main Content */}
                <Tabs defaultValue="submitted" className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="submitted">
                            Submitted Tools
                        </TabsTrigger>
                        <TabsTrigger value="saved">Saved Tools</TabsTrigger>
                    </TabsList>

                    {/* Submitted Tools Tab */}
                    <TabsContent value="submitted" className="space-y-6">
                        <Card>
                            <div className="p-6">
                                <h2 className="text-lg font-semibold mb-4">
                                    Submitted Tools
                                </h2>
                                {renderSubmittedTools()}
                            </div>
                        </Card>
                    </TabsContent>

                    {/* Saved Tools Tab */}
                    <TabsContent value="saved">
                        <Card>
                            <div className="p-6">
                                <h2 className="text-lg font-semibold mb-4">
                                    Saved Tools
                                </h2>
                                {renderSavedTools()}
                            </div>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    );
};

const mapStateToProps = (state) => ({
    userAuth: state.auth.userAuth,
    savedTools: state.tool.savedTools,
});

export default connect(mapStateToProps, { fetchSavedTools })(Dashboard);
